import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Space,
  Popconfirm,
  Tag,
  Form,
  Input,
  InputNumber,
  Upload,
  Spin,
  Alert,
  Avatar,
  Select,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useCourse } from "../contexts/CourseContext";
import { useNotification } from "../contexts/NotificationContext";
import { useCategory } from "../contexts/CategoryContext";
import ScrollableTable from "../components/ScrollableTable";

const { TextArea } = Input;
const { Option } = Select;

const Courses: React.FC = () => {
  const {
    courses,
    loading,
    error,
    meta,
    fetchCourses,
    createCourse,
    updateCourse,
    deleteCourse,
  } = useCourse();
  const { showError } = useNotification();
  const {
    categories,
    fetchCategories,
    loading: categoriesLoading,
  } = useCategory();

  const [modalVisible, setModalVisible] = useState(false);
  const [editingCourse, setEditingCourse] = useState<any>(null);
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    fetchCourses({ page, limit });
    // eslint-disable-next-line
  }, [page, limit]);

  // Fetch categories when modal opens (if not already loaded)
  useEffect(() => {
    if (modalVisible && categories.length === 0) {
      fetchCategories();
    }
    // eslint-disable-next-line
  }, [modalVisible]);

  const handleAdd = () => {
    setEditingCourse(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingCourse(record);
    form.setFieldsValue({
      ...record,
      titleEn: record.title.en,
      titleAr: record.title.ar,
      descriptionEn: record.description?.en,
      descriptionAr: record.description?.ar,
      originalPrice: record.originalPrice,
      price: record.price,
      level: record.level,
      language: record.language,
      instructorName: record.instructor.name,
      instructorBioEn: record.instructor.bio?.en,
      instructorBioAr: record.instructor.bio?.ar,
      students: record.students,
      rating: record.rating,
      subtitle: record.subtitle,
      aboutEn: record.about?.en,
      aboutAr: record.about?.ar,
      whatYouLearnEn: record.whatYouLearn?.en || [],
      whatYouLearnAr: record.whatYouLearn?.ar || [],
      category: record.category,
      enrollUrl: record.enrollUrl || "",
      schedule: record.schedule || "",
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    await deleteCourse(id);
    fetchCourses({ page, limit });
  };

  const handleFinish = async (values: any) => {
    // Robust number validation
    const price = Number(values.price);
    const originalPrice = Number(values.originalPrice);
    const rating = Number(values.rating);
    const students = Number(values.students);

    if (
      isNaN(price) ||
      isNaN(originalPrice) ||
      isNaN(rating) ||
      price === null ||
      originalPrice === null ||
      rating === null
    ) {
      showError("Price, Original Price, and Rating must be valid numbers.");
      return;
    }

    const courseData = {
      title: {
        en: values.titleEn,
        ar: values.titleAr,
      },
      description: {
        en: values.descriptionEn,
        ar: values.descriptionAr,
      },
      originalPrice,
      price,
      level: values.level,
      language: values.language,
      category: values.category,
      subtitle: values.subtitle || "",
      students: String(students),
      rating,
      about: {
        en: values.aboutEn || "",
        ar: values.aboutAr || "",
      },
      lastUpdate: new Date().toISOString(),
      whatYouLearn: {
        en: values.whatYouLearnEn || [],
        ar: values.whatYouLearnAr || [],
      },
      instructor: {
        name: values.instructorName,
        bio: {
          en: values.instructorBioEn || "",
          ar: values.instructorBioAr || "",
        },
      },
      enrollUrl: values.enrollUrl || "",
      schedule: values.schedule || "",
    };

    const files = {
      courseImage: values.courseImage?.[0]?.originFileObj,
      instructorAvatar: values.instructorAvatar?.[0]?.originFileObj,
    };

    if (editingCourse) {
      await updateCourse(editingCourse._id, courseData, files);
    } else {
      await createCourse(courseData, files);
    }
    setModalVisible(false);
    fetchCourses({ page, limit });
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "_id",
      key: "id",
      render: (id: string) => `#${id.slice(-8)}`,
    },
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (img: string) =>
        img && <Avatar shape="square" size={48} src={img} />,
    },
    {
      title: "Title",
      dataIndex: ["title", "en"],
      key: "title",
      render: (_: any, record: any) => record.title.en,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (cat: string) => <Tag color="blue">{cat}</Tag>,
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price: number) => `$${price}`,
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
    },
    {
      title: "Instructor",
      dataIndex: ["instructor", "name"],
      key: "instructor",
      render: (_: any, record: any) => record.instructor.name,
    },
    {
      title: "Avatar",
      dataIndex: ["instructor", "avatar"],
      key: "avatar",
      render: (_: any, record: any) =>
        record.instructor.avatar ? (
          <Avatar size={40} src={record.instructor.avatar} />
        ) : null,
    },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Are you sure to delete this course?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h2>Courses</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Course
        </Button>
      </div>
      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} />
      )}
      <Spin spinning={loading} tip="Loading courses...">
        <ScrollableTable
          rowKey="_id"
          columns={columns}
          dataSource={courses}
          pagination={{
            current: meta?.page || 1,
            pageSize: meta?.limit || 10,
            total: meta?.total || 0,
            onChange: (p, ps) => {
              setPage(p);
              setLimit(ps);
            },
            showSizeChanger: true,
          }}
        />
      </Spin>
      <Modal
        open={modalVisible}
        title={editingCourse ? "Edit Course" : "Add Course"}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
        width={800}
      >
        <Button
          style={{ marginBottom: 16 }}
          onClick={() => {
            form.setFieldsValue({
              titleEn: "Test Course EN",
              titleAr: "دورة اختبار",
              descriptionEn: "This is a test course description in English.",
              descriptionAr: "هذا وصف دورة اختبارية بالعربية.",
              originalPrice: 200,
              price: 100,
              level: "Beginner",
              language: "English",
              category: "web-development",
              subtitle: "Test Subtitle",
              students: 123,
              rating: 4.5,
              aboutEn: "About this test course (EN)",
              aboutAr: "حول هذه الدورة الاختبارية (AR)",
              whatYouLearnEn: ["Learn React", "Learn Node.js"],
              whatYouLearnAr: ["تعلم ريأكت", "تعلم نود جي اس"],
              instructorName: "John Doe",
              instructorBioEn: "Test instructor bio EN",
              instructorBioAr: "سيرة المدرب الاختبارية",
              enrollUrl: "https://example.com/enroll",
              schedule: `Course Schedule:

Week 1: Introduction to the course
• Overview of the curriculum
• Setting up development environment
• Basic concepts introduction

Week 2: Basic concepts and fundamentals
• Core principles and theories
• Hands-on exercises
• Assignment 1

Week 3: Advanced topics and practical exercises
• Complex scenarios
• Real-world applications
• Assignment 2

Week 4: Final project and assessment
• Project development
• Final presentation
• Course evaluation`,
            });
          }}
        >
          Auto-Fill Test Data
        </Button>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            price: 0,
            originalPrice: 0,
            rating: 0,
            students: "0",
            whatYouLearnEn: [],
            whatYouLearnAr: [],
          }}
        >
          <Form.Item
            label="Title (EN)"
            name="titleEn"
            rules={[
              { required: true, message: "Please enter the English title" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Title (AR)"
            name="titleAr"
            rules={[
              { required: true, message: "Please enter the Arabic title" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Description (EN)"
            name="descriptionEn"
            rules={[
              {
                required: true,
                message: "Please enter the English description",
              },
            ]}
          >
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            label="Description (AR)"
            name="descriptionAr"
            rules={[
              {
                required: true,
                message: "Please enter the Arabic description",
              },
            ]}
          >
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            label="Original Price"
            name="originalPrice"
            rules={[
              { required: true, message: "Please enter the original price" },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Price"
            name="price"
            rules={[
              {
                required: true,
                type: "number",
                message: "Please enter the price",
              },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Level"
            name="level"
            rules={[{ required: true, message: "Please enter the level" }]}
          >
            <Input placeholder="e.g. Beginner, Intermediate, Advanced" />
          </Form.Item>
          <Form.Item
            label="Language"
            name="language"
            rules={[{ required: true, message: "Please enter the language" }]}
          >
            <Input placeholder="e.g. English, Arabic, French, Spanish" />
          </Form.Item>
          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select
              loading={categoriesLoading}
              placeholder="Select a category"
              showSearch
              optionFilterProp="children"
            >
              {categories.map((cat) => (
                <Option key={cat.slug} value={cat.slug}>
                  {cat.title.en}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Subtitle" name="subtitle">
            <Input />
          </Form.Item>
          <Form.Item label="Enrollment URL" name="enrollUrl">
            <Input placeholder="https://example.com/enroll" />
          </Form.Item>
          <Form.Item label="Schedule" name="schedule">
            <TextArea
              rows={8}
              placeholder="Enter course schedule... You can use basic formatting like:
• Week 1: Introduction
• Week 2: Basic concepts
• Week 3: Advanced topics
• Week 4: Final project"
              style={{ fontFamily: "monospace" }}
            />
          </Form.Item>
          <Form.Item
            label="Students"
            name="students"
            rules={[
              {
                required: true,
                message: "Please enter the number of students",
              },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Rating"
            name="rating"
            rules={[{ required: true, message: "Please enter the rating" }]}
          >
            <InputNumber min={0} max={5} step={0.1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="About (EN)" name="aboutEn">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item label="About (AR)" name="aboutAr">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item label="What You Learn (EN)" required>
            <Form.List name="whatYouLearnEn">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline">
                      <Form.Item
                        {...field}
                        name={[field.name]}
                        rules={[
                          {
                            required: true,
                            message: "Please enter a learning outcome",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Learning outcome (EN)" />
                      </Form.Item>
                      <Button
                        onClick={() => remove(field.name)}
                        icon={<DeleteOutlined />}
                        type="text"
                        danger
                      />
                    </Space>
                  ))}
                  <Button
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    type="dashed"
                    style={{ width: "100%", marginTop: 8 }}
                  >
                    Add What You Learn (EN)
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item label="What You Learn (AR)" required>
            <Form.List name="whatYouLearnAr">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline">
                      <Form.Item
                        {...field}
                        name={[field.name]}
                        rules={[
                          {
                            required: true,
                            message: "Please enter a learning outcome",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Learning outcome (AR)" />
                      </Form.Item>
                      <Button
                        onClick={() => remove(field.name)}
                        icon={<DeleteOutlined />}
                        type="text"
                        danger
                      />
                    </Space>
                  ))}
                  <Button
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    type="dashed"
                    style={{ width: "100%", marginTop: 8 }}
                  >
                    Add What You Learn (AR)
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item label="Instructor Name" name="instructorName">
            <Input />
          </Form.Item>
          <Form.Item label="Instructor Bio (EN)" name="instructorBioEn">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Instructor Bio (AR)" name="instructorBioAr">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            label="Course Image"
            name="courseImage"
            valuePropName="fileList"
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
          >
            <Upload beforeUpload={() => false} maxCount={1} listType="picture">
              <Button icon={<UploadOutlined />}>Upload Course Image</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            label="Instructor Avatar"
            name="instructorAvatar"
            valuePropName="fileList"
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
          >
            <Upload beforeUpload={() => false} maxCount={1} listType="picture">
              <Button icon={<UploadOutlined />}>
                Upload Instructor Avatar
              </Button>
            </Upload>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editingCourse ? "Update" : "Create"} Course
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Courses;
